<template>
  <div class="comet-i18n-exclude">
    <custom-field-label :field="{ label: t('companySelect.carrierLabel'), required: true }" />

    <div class="company-select-container">
      <v-autocomplete
        required
        variant="outlined"
        color="primary"
        v-model="selectedCompany"
        @update:search="onSearchInput"
        @update:model-value="$emit('update:model-value', selectedCompany)"
        @blur="onBlur"
        @click:clear="companies = []"
        v-model:search="searchStr"
        data-testid="comet-asset-company-field"
        :items="companies"
        :placeholder="t('companySelect.searchPlaceholder')"
        item-value="id"
        item-title="name"
        return-object
        clearable
        no-filter
        :menu-props="{ attach: '.company-select-container' }"
        ref="companySelect"
        persistent-hint
        :no-auto-scroll="true"
        :loading="isLoading">
        <template
          v-slot:prepend-item
          v-if="!isLoading && searchStr?.length > 0 && companies.length > 0">
          <v-list-item
            data-testid="comet-use-custom-company"
            class="add-custom-company"
            role="option"
            @click="addCustomCompany">
            <v-list-item-title>
              {{ t('companySelect.selectAsCarrier', { name: searchStr }) }}
            </v-list-item-title>
          </v-list-item>
          <v-divider v-if="!isLoading && searchStr" />
        </template>

        <template v-slot:item="{ item, props }">
          <v-list-item v-bind="props" :title="null" role="option">
            <v-list-item-title v-html="`${item.raw.name}`"></v-list-item-title>
          </v-list-item>

          <v-divider
            v-if="companies.indexOf(item.raw) < companies.length - 1"
            :key="companies.indexOf(item.raw)" />
        </template>

        <template #no-data>
          <v-list-item
            data-testid="comet-use-custom-company"
            role="option"
            class="add-custom-company"
            v-if="!isLoading && searchStr?.length >= 1 && companies.length === 0"
            @click="addCustomCompany">
            <v-list-item-title>
              {{ t('companySelect.selectAsCarrier', { name: searchStr }) }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-else>{{ t('companySelect.typeToSearch') }}</v-list-item>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';

import { defineComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAxiosClient } from './composables';

export default defineComponent({
  props: {
    warehouseId: {
      type: String,
      required: true
    },
    /** Restores selection when the component remounts (e.g. returning from ID verification). */
    modelValue: {
      type: Object,
      default: null
    }
  },
  emits: ['update:model-value'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const companies = ref([]);
    const count = ref(0);
    const total = ref(0);
    const page = ref(1);
    const limit = ref(30);
    const searchStr = ref(null);

    const selectedCompany = ref(null);
    const isLoading = ref(false);
    const companySelect = ref(null);
    const axiosClient = useAxiosClient();

    watch(
      () => props.modelValue,
      value => {
        if (value && (value.name || value.id)) {
          selectedCompany.value = {
            id: value.id ?? null,
            name: typeof value.name === 'string' ? value.name : ''
          };
        } else {
          selectedCompany.value = null;
        }
      },
      { immediate: true }
    );

    const getData = async () => {
      isLoading.value = true;

      if (!searchStr.value) {
        isLoading.value = false;
        companies.value = [];
        return;
      }

      const offset = (page.value - 1) * limit.value;
      const payload = {
        search: searchStr.value,
        pagination: { limit: limit.value, offset }
      };
      try {
        const response = await axiosClient.post(
          `/checkin/public/${props.warehouseId}/company-autocomplete`,
          payload
        );

        total.value = get(response, 'data.meta.totalRecords', 0);
        page.value = get(response, 'data.meta.page', 1);
        count.value = get(response, 'data.items.length', 0);

        const items = get(response, 'data.items', []);

        companies.value = items.map(item => ({
          raw: item,
          id: item.id,
          name: item.name,
          scac: item.scac
        }));
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        isLoading.value = false;
      }
    };

    const onSearchInput = async inputVal => {
      searchStr.value = inputVal;
      if (inputVal?.length >= 1) {
        getData();
      }

      if (!inputVal?.length) {
        companies.value = [];
      }
    };

    const addCustomCompany = () => {
      if (searchStr.value) {
        selectedCompany.value = {
          id: null,
          name: searchStr.value
        };

        if (companySelect.value && companySelect.value.blur) {
          companySelect.value.blur();
        }

        emit('update:model-value', selectedCompany.value);
        companies.value = [];
      }
    };

    const onBlur = () => {
      const typed = searchStr.value?.trim();
      if (typed && typed !== selectedCompany.value?.name) {
        addCustomCompany();
      } else {
        companies.value = [];
      }
    };

    return {
      t,
      companies,
      count,
      total,
      page,
      limit,
      searchStr,
      selectedCompany,
      isLoading,
      companySelect,
      addCustomCompany,
      onBlur,
      getData,
      onSearchInput
    };
  }
});
</script>
<style lang="scss">
.add-custom-company {
  min-height: 60px !important;
  position: sticky;
  top: -8px;
  z-index: 2;
  background: $white;
}
.company-select-container {
  position: relative;
}
.company-select-container .v-menu > .v-overlay__content.v-autocomplete__content {
  top: 57px !important;
}
</style>
